import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { Input } from "@/components/core/forms/input";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { formatNumber } from "@/utils/format-number";
import { IconDownload, IconSearch, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { z } from "zod";

interface ProjectReport {
	id: string;
	no: number;
	account_name: string;
	project_name: string;
	project_description: string;
	client_name: string;
	date_of_contract: string;
	contract_duration: string;
	billing_periods: string[];
	total_contract_price: number;
	project_status: string;
	remarks: string;
	payment_type: string | null;
	project_type: string;
}

interface Salesperson {
	id: string;
	name: string;
}

interface SummaryRow {
	account_name: string;
	active: number;
	active_amount: number;
	completed: number;
	completed_amount: number;
	expired: number;
	expired_amount: number;
	cancelled: number;
	cancelled_amount: number;
}

const columns: ColumnDef<ProjectReport>[] = [
	{
		id: "no",
		label: "No",
		cellClassName: "w-[50px] text-center",
		headerClassName: "whitespace-nowrap text-center",
		cell: ({ data }) => <span>{data.no}</span>,
	},
	{
		id: "account_name",
		label: "Account Name",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="font-medium text-gray-900">{data.account_name}</span>,
	},
	{
		id: "project_name",
		label: "Project Name (Official Project Name)",
		cellClassName: "min-w-[300px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<div className="flex flex-col">
				<span className="font-semibold text-gray-900">{data.project_name}</span>
				{data.project_description && <span className="text-xs text-gray-500">{data.project_description}</span>}
			</div>
		),
	},
	{
		id: "client_name",
		label: "Client Name",
		cellClassName: "min-w-[150px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.client_name}</span>,
	},
	{
		id: "date_of_contract",
		label: "Date of Contract",
		cellClassName: "min-w-[200px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900 whitespace-pre-line text-xs">{data.date_of_contract}</span>,
	},
	{
		id: "contract_duration",
		label: "Contract Duration",
		cellClassName: "min-w-[140px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900">{data.contract_duration}</span>,
	},
	{
		id: "billing_periods",
		label: "Billing Period",
		cellClassName: "min-w-[220px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <BillingPeriodCell periods={data.billing_periods} />,
	},
	{
		id: "total_contract_price",
		label: "Total Contract Price",
		cellClassName: "min-w-[160px] text-right",
		headerClassName: "whitespace-nowrap text-right",
		cell: ({ data }) => <span className="font-medium text-gray-900">&#8369;{formatNumber(data.total_contract_price)}</span>,
	},
	{
		id: "project_status",
		label: "Contract Status",
		cellClassName: "min-w-[120px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => {
			const statusColors: Record<string, string> = {
				active: "bg-green-100 text-green-800",
				expired: "bg-red-100 text-red-800",
				cancelled: "bg-gray-100 text-gray-800",
				completed: "bg-blue-100 text-blue-800",
			};
			return (
				<span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[data.project_status] || "bg-gray-100 text-gray-800"}`}>
					{data.project_status.charAt(0).toUpperCase() + data.project_status.slice(1)}
				</span>
			);
		},
	},
	{
		id: "remarks",
		label: "Remarks",
		cellClassName: "min-w-[150px]",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-900 text-sm">{data.remarks || "-"}</span>,
	},
];

const VISIBLE_PERIOD_LIMIT = 12;

function BillingPeriodCell({ periods }: { periods: string[] }) {
	const [expanded, setExpanded] = useState(false);

	if (periods.length === 0) {
		return <span className="text-gray-900 text-xs">-</span>;
	}

	const showToggle = periods.length > VISIBLE_PERIOD_LIMIT;
	const visible = expanded ? periods : periods.slice(0, VISIBLE_PERIOD_LIMIT);

	return (
		<div className="flex flex-col gap-0.5">
			{visible.map((period) => (
				<span key={period} className="text-gray-900 text-xs">
					{period}
				</span>
			))}
			{showToggle && (
				<button type="button" onClick={() => setExpanded((prev) => !prev)} className="text-blue-600 text-xs font-medium hover:underline text-left">
					{expanded ? "Show less" : `+${periods.length - VISIBLE_PERIOD_LIMIT} more`}
				</button>
			)}
		</div>
	);
}

const STATUS_COLUMNS = [
	{ key: "active", label: "Active", className: "bg-green-100 text-green-800" },
	{ key: "completed", label: "Completed", className: "bg-blue-100 text-blue-800" },
	{ key: "expired", label: "Expired", className: "bg-red-100 text-red-800" },
	{ key: "cancelled", label: "Cancelled", className: "bg-gray-100 text-gray-800" },
] as const;

function SummaryTable({ data }: { data: SummaryRow[] }) {
	if (data.length === 0) {
		return <div className="text-center py-8 text-gray-500">No data available</div>;
	}

	return (
		<div className="overflow-x-auto border rounded-lg">
			<table className="w-full">
				<thead>
					<tr className="border-b">
						<th className="text-left px-4 py-3 text-sm font-semibold text-gray-900 bg-gray-50">Account Name</th>
						{STATUS_COLUMNS.map((col) => (
							<th key={col.key} className="px-4 py-3 text-center text-sm font-semibold min-w-[120px]">
								<span className={`px-3 py-1 rounded-full text-xs font-medium ${col.className}`}>{col.label}</span>
							</th>
						))}
						<th className="px-4 py-3 text-center text-sm font-semibold text-gray-900 bg-gray-50">Total</th>
					</tr>
				</thead>
				<tbody>
					{data.map((row) => {
						const totalCount = row.active + row.completed + row.expired + row.cancelled;
						const totalAmount = row.active_amount + row.completed_amount + row.expired_amount + row.cancelled_amount;
						return (
							<tr key={row.account_name} className="border-b hover:bg-gray-50">
								<td className="px-4 py-3 text-sm font-semibold text-gray-900">{row.account_name}</td>
								{STATUS_COLUMNS.map((col) => {
									const count = row[col.key] || 0;
									const amount = (row[`${col.key}_amount` as keyof SummaryRow] as number) || 0;
									return (
										<td key={col.key} className="px-4 py-3 text-center text-sm text-gray-900">
											<div>{count}</div>
											{count > 0 && <div className="text-xs text-gray-500">&#8369;{formatNumber(amount)}</div>}
										</td>
									);
								})}
								<td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
									<div>{totalCount}</div>
									<div className="text-xs text-gray-500">&#8369;{formatNumber(totalAmount)}</div>
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export const Route = createFileRoute("/_dashboard/report-projects")({
	validateSearch: z.object({
		search: z.string().optional(),
		status: z.string().optional(),
		salesperson_id: z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().min(1).optional(),
	}),
	component: ReportProjectsPage,
});

function ReportProjectsPage() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [searchTerm, setSearchTerm] = useState(search.search || "");

	const salespersonsQuery = useQuery({
		queryKey: ["report-projects-salespersons"],
		queryFn: async () => {
			const response = await fetch("/api/reports/projects/salespersons");
			return response.json() as Promise<Salesperson[]>;
		},
	});

	const isAllTab = !search.salesperson_id;

	const summaryQuery = useQuery({
		queryKey: ["report-projects-summary"],
		queryFn: async () => {
			const response = await fetch("/api/reports/projects/summary");
			return response.json() as Promise<SummaryRow[]>;
		},
		enabled: isAllTab,
	});

	const reportQuery = useQuery({
		queryKey: ["report-projects", search],
		queryFn: async () => {
			const params = new URLSearchParams();
			if (search.search) params.append("search", search.search);
			if (search.status) params.append("status", search.status);
			if (search.salesperson_id) params.append("salesperson_id", search.salesperson_id);
			if (search.per_page) params.append("per_page", String(search.per_page));
			if (search.page) params.append("page", String(search.page));
			const response = await fetch(`/api/reports/projects?${params}`);
			return response.json();
		},
		enabled: !isAllTab,
	});

	const handleSearch = async () => {
		await navigate({
			to: "/report-projects",
			search: (old) => ({
				...old,
				search: searchTerm || undefined,
				page: 1,
			}),
		});
	};

	const handleClearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/report-projects",
			search: (old) => ({
				...old,
				search: undefined,
				page: 1,
			}),
		});
	};

	const handleSalespersonTab = async (salespersonId: string | undefined) => {
		await navigate({
			to: "/report-projects",
			search: (old) => ({
				...old,
				salesperson_id: salespersonId,
				page: 1,
			}),
		});
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			handleSearch();
		}
	};

	const handleExportExcel = () => {
		const params = new URLSearchParams();
		if (search.search) params.append("search", search.search);
		if (search.status) params.append("status", search.status);
		if (search.salesperson_id) params.append("salesperson_id", search.salesperson_id);
		window.open(`/api/reports/projects/export/excel?${params.toString()}`, "_blank");
	};

	const data = reportQuery.data?.data || [];
	const meta = reportQuery.data?.meta || {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 25,
	};

	const salespersons = salespersonsQuery.data || [];

	const isLoading = isAllTab ? summaryQuery.isLoading : reportQuery.isLoading;

	if (isLoading) {
		return (
			<Content pageName="Sales Contracts Report">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading report...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Sales Contracts Report">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-col gap-4">
					<div className="flex flex-row items-center justify-between w-full gap-4">
						<div className="inline-flex flex-col w-auto">
							<span className="text-xl text-foreground-dark font-semibold">Sales Contracts Report</span>
							<span className="text-base text-foreground/70">Overview of all sales contracts with details</span>
						</div>
						<Button variant="outline" onClick={handleExportExcel}>
							<IconDownload size={18} stroke={2} />
							<span>Export Excel</span>
						</Button>
					</div>

					<div className="flex gap-2">
						<Input
							placeholder="Search by contract, project name, client, or salesperson..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyPress={handleKeyPress}
							className="flex-1 border"
						/>
						<Button onClick={handleSearch} disabled={!searchTerm.trim()}>
							<IconSearch size={18} stroke={2} />
						</Button>
						{searchTerm && (
							<Button variant="outline" onClick={handleClearSearch}>
								<IconX size={18} stroke={2} />
							</Button>
						)}
					</div>
				</div>

				{/* Salesperson Tabs */}
				{salespersons.length > 0 && (
					<div className="flex items-center gap-1 overflow-x-auto border-b border-border/50 pb-px">
						<button
							type="button"
							onClick={() => handleSalespersonTab(undefined)}
							className={clsx(
								"px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-md border border-b-0 transition-colors",
								!search.salesperson_id
									? "bg-white text-primary-700 border-border/50 -mb-px"
									: "bg-gray-50 text-foreground/60 border-transparent hover:bg-gray-100 hover:text-foreground",
							)}
						>
							All
						</button>
						{salespersons.map((sp) => (
							<button
								key={sp.id}
								type="button"
								onClick={() => handleSalespersonTab(sp.id)}
								className={clsx(
									"px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-md border border-b-0 transition-colors",
									search.salesperson_id === sp.id
										? "bg-white text-primary-700 border-border/50 -mb-px"
										: "bg-gray-50 text-foreground/60 border-transparent hover:bg-gray-100 hover:text-foreground",
								)}
							>
								{sp.name}
							</button>
						))}
					</div>
				)}

				{isAllTab ? (
					<SummaryTable data={summaryQuery.data || []} />
				) : (
					<div className="flex flex-col w-full gap-4">
						<DataTable
							className="border-collapse"
							data={data}
							columns={columns}
							stickyHead={false}
							stickyCol={false}
							showBorder={true}
							compact={true}
							showToolbar={false}
						/>
						<Pagination
							onClick={async (page) => {
								await navigate({
									to: "/report-projects",
									search: (old) => ({
										...old,
										page,
									}),
								});
							}}
							initialPage={meta.current_page}
							pageCount={meta.last_page}
						/>
					</div>
				)}
			</div>
		</Content>
	);
}
